import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Mail, MessageCircle, Check, X, TestTube, Eye, EyeOff, Zap, QrCode, Wifi, AlertTriangle, RefreshCw, Loader2, Brain, GitBranch, Settings, Key, Play, AlertCircle, Square, History, ChevronDown, ChevronRight, Clock, User, Bot, FileText, XCircle, Phone, ExternalLink, Copy, FolderOpen, Plus, Pencil, Trash2, Info, Receipt } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import ContractTemplateEditor from "../components/ContractTemplateEditor";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pulsing status indicator with error tooltip
function StatusDot({ status, errorMsg }) {
  const configs = {
    connected:  { dot: "#10b981", label: "Conectado" },
    connecting: { dot: "#f59e0b", label: "Aguardando QR Code" },
    disabled:   { dot: "#94a3b8", label: "Desativado" },
    error:      { dot: "#ef4444", label: "Erro" },
  };
  const cfg = configs[status] || configs.disabled;

  const dot = (
    <div className="flex items-center gap-2">
      <div className="relative w-3 h-3 flex-shrink-0">
        <span className="absolute inset-0 rounded-full animate-ping opacity-60" style={{ backgroundColor: cfg.dot }} />
        <span className="absolute inset-0 rounded-full" style={{ backgroundColor: cfg.dot }} />
      </div>
      <span className="text-xs font-medium" style={{ color: cfg.dot }}>{cfg.label}</span>
    </div>
  );

  if (status === "error" && errorMsg) {
    return (
      <Tooltip>
        <TooltipTrigger asChild><div className="cursor-default">{dot}</div></TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{errorMsg}</TooltipContent>
      </Tooltip>
    );
  }
  return dot;
}

function WhatsAppEvolutionCard({ tc500, tc50 }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("disabled");
  const [enabled, setEnabled] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const pollRef = useRef(null);
  const isEnablingRef = useRef(false);
  const autoOpenQrRef = useRef(false);

  // Lead pipeline config
  const [leadPipelineId, setLeadPipelineId] = useState("");
  const [leadStageId, setLeadStageId] = useState("");
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [leadConfigSaving, setLeadConfigSaving] = useState(false);

  // Lead config modal
  const [leadConfigOpen, setLeadConfigOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/status`, { withCredentials: true });
      const s = res.data.status;
      const err = res.data.error || "";
      setStatus(prev => {
        // Flag to auto-open QR only when user just enabled (not on page load)
        if (isEnablingRef.current && prev !== "connecting" && s === "connecting") {
          autoOpenQrRef.current = true;
        }
        return s;
      });
      setErrorMsg(err);
      if (!isEnablingRef.current) {
        setEnabled(s !== "disabled");
      }
    } catch (e) {
      setStatus("error");
      setErrorMsg(e.message || "Erro ao verificar status");
    } finally {
      setInitialLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll every 3 seconds when connecting
  useEffect(() => {
    if (status === "connecting") {
      pollRef.current = setInterval(fetchStatus, 3000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [status, fetchStatus]);

  // Fetch stages for a given pipeline
  const fetchStages = useCallback(async (pipelineId) => {
    if (!pipelineId) { setStages([]); return; }
    try {
      const res = await axios.get(`${API}/pipeline-stages`, { params: { pipeline_id: pipelineId }, withCredentials: true });
      setStages(res.data || []);
    } catch { setStages([]); }
  }, []);

  // Fetch pipelines and lead config
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/pipelines`, { withCredentials: true }),
      axios.get(`${API}/whatsapp/lead-config`, { withCredentials: true }),
    ]).then(([pRes, lcRes]) => {
      setPipelines(pRes.data || []);
      setLeadPipelineId(lcRes.data?.pipeline_id || "");
      setLeadStageId(lcRes.data?.stage_id || "");
      if (lcRes.data?.pipeline_id) {
        fetchStages(lcRes.data.pipeline_id);
      }
    }).catch(() => {});
  }, [fetchStages]);

  const handleEnable = async () => {
    setLoadingToggle(true);
    isEnablingRef.current = true;
    try {
      await axios.post(`${API}/whatsapp/enable`, {}, { withCredentials: true });
      // Instance created — set as enabled and open QR immediately
      setEnabled(true);
      setStatus("connecting");
      setLoadingToggle(false);
      isEnablingRef.current = false;
      autoOpenQrRef.current = true;
      fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao ativar WhatsApp";
      toast.error(msg);
      setLoadingToggle(false);
      isEnablingRef.current = false;
    }
  };

  const handleDisable = async () => {
    setDisableConfirmOpen(false);
    setLoadingToggle(true);
    try {
      await axios.delete(`${API}/whatsapp/disable`, { withCredentials: true });
      setEnabled(false);
      setStatus("disabled");
      toast.success("WhatsApp desativado.");
    } catch (err) {
      toast.error("Erro ao desativar WhatsApp");
    } finally { setLoadingToggle(false); }
  };

  const handleToggle = (val) => {
    if (val) { handleEnable(); }
    else { setDisableConfirmOpen(true); }
  };

  const openQrCode = async () => {
    setLoadingQr(true);
    setQrOpen(true);
    try {
      const res = await axios.get(`${API}/whatsapp/qrcode`, { withCredentials: true });
      setQrCode(res.data.qr_code);
    } catch (err) {
      toast.error("Erro ao carregar QR Code");
      setQrOpen(false);
    } finally { setLoadingQr(false); }
  };

  // Auto-open QR modal when flag is set by fetchStatus
  useEffect(() => {
    if (autoOpenQrRef.current) {
      autoOpenQrRef.current = false;
      setTimeout(() => openQrCode(), 400);
    }
    // Auto-close QR modal when connected
    if (status === "connected" && qrOpen) {
      setQrOpen(false);
      setQrCode(null);
      toast.success("WhatsApp conectado com sucesso!");
    }
  }, [status]); // eslint-disable-line

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setLeadConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <MessageCircle className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">WhatsApp API</p>
              <Badge className={status === "connected" ? "bg-emerald-100 text-emerald-700 border-0" : status === "connecting" ? "bg-amber-100 text-amber-700 border-0" : status === "error" ? "bg-rose-100 text-rose-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {status === "connected" ? "Conectado" : status === "connecting" ? "Aguardando QR" : status === "error" ? "Erro" : "Desconectado"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Habilite para interagir usando o WhatsApp.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setLeadConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      {/* WhatsApp Config Modal — sidebar layout like GoTo */}
      <Dialog open={leadConfigOpen} onOpenChange={setLeadConfigOpen}>
        <DialogContent className="max-w-2xl !p-0 overflow-hidden bg-white">
          <div className="flex h-[520px]">
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">WhatsApp API</p>
              <div className="space-y-0.5">
                <button className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: tc500 }}>
                  <Key className="w-4 h-4" />Autenticação
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação WhatsApp
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Integração via API não oficial da Meta que conecta o WhatsApp da sua empresa. O uso inadequado (spam, mensagens em massa) pode resultar em bloqueio do número.</p>
              </div>

              {status === "connected" ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                  <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                </div>
              ) : status === "connecting" ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <p className="font-semibold flex items-center gap-2"><AlertCircle className="w-4 h-4" />Aguardando QR Code</p>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  Não conectado. Ative o switch e escaneie o QR Code.
                </div>
              )}

              <div className="flex gap-2">
                {!initialLoading && !loadingToggle && enabled && status !== "connected" && status !== "disabled" && (
                  <Button onClick={openQrCode} disabled={loadingQr} variant="outline" size="sm" className="text-xs gap-1.5">
                    {loadingQr ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}Escanear QR Code
                  </Button>
                )}
                {enabled && (
                  <Button onClick={() => { setDisableConfirmOpen(true); }} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                    <XCircle className="w-3 h-3" />Desconectar
                  </Button>
                )}
                {!enabled && (
                  <Button onClick={() => handleToggle(true)} disabled={loadingToggle} className="text-white text-xs gap-1.5" style={{ backgroundColor: tc500 }}>
                    {loadingToggle ? <><Loader2 className="w-3 h-3 animate-spin" />Ativando WhatsApp...</> : <><Check className="w-3 h-3" />Ativar WhatsApp</>}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <QrCode className="w-5 h-5" style={{ color: tc500 }} />
              Escanear QR Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 text-center">
              Abra o WhatsApp no seu celular → <strong>Dispositivos conectados</strong> → <strong>Conectar dispositivo</strong> → escaneie o código abaixo.
            </p>
            <div className="flex items-center justify-center bg-slate-50 rounded-xl p-4 min-h-[240px]">
              {loadingQr ? (
                <div className="text-slate-400 text-sm">Carregando QR Code...</div>
              ) : qrCode ? (
                <img
                  src={qrCode.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code WhatsApp"
                  className="w-48 h-48 object-contain"
                />
              ) : (
                <div className="text-slate-400 text-sm text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                  QR Code não disponível
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-400 text-center">
              O QR Code expira em 60 segundos. Se expirar, feche e abra novamente.
            </p>
            <Button variant="outline" className="w-full" onClick={() => { setQrOpen(false); setQrCode(null); }}>
              <X className="w-4 h-4 mr-2" />Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation Modal */}
      <Dialog open={disableConfirmOpen} onOpenChange={setDisableConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Desativar WhatsApp?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-rose-700">Atenção — isso irá afetar:</p>
              <ul className="text-sm text-rose-600 space-y-1 ml-4 list-disc">
                <li>Todas as automações que enviam mensagens WhatsApp</li>
                <li>O histórico de conexão será perdido</li>
                <li>Será necessário escanear o QR Code novamente ao reativar</li>
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              A instância WhatsApp será <strong>removida permanentemente</strong>. Tem certeza?
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDisableConfirmOpen(false)}>
                <X className="w-4 h-4 mr-2" />Cancelar
              </Button>
              <Button className="flex-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDisable}>
                <Check className="w-4 h-4 mr-2" />Sim, desativar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmailSMTPCard({ tc500, tc50 }) {
  const { t } = useTranslation();
  const [emailConfig, setEmailConfig] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [configOpen, setConfigOpen] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState("loading"); // loading | inactive | problem | active

  const checkSmtpStatus = useCallback(async (cfg) => {
    if (!cfg || !cfg.enabled) { setSmtpStatus("inactive"); return; }
    const c = cfg.config || {};
    if (!c.host || !c.port || !c.username || !c.password) { setSmtpStatus("inactive"); return; }
    try {
      setSmtpStatus("loading");
      const res = await axios.post(`${API}/integrations/email/test`, {}, { withCredentials: true });
      setSmtpStatus(res.data.ok ? "active" : "problem");
    } catch { setSmtpStatus("problem"); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/integrations/email`, { withCredentials: true })
      .then(res => { setEmailConfig(res.data); checkSmtpStatus(res.data); })
      .catch(() => { setEmailConfig({ enabled: false, config: {} }); setSmtpStatus("inactive"); });
  }, [checkSmtpStatus]);

  const save = async () => {
    try {
      await axios.put(`${API}/integrations/email`, emailConfig, { withCredentials: true });
      toast.success("E-mail salvo!");
      setConfigOpen(false);
      checkSmtpStatus(emailConfig);
    } catch { toast.error("Erro ao salvar"); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/integrations/email/test`, {}, { withCredentials: true });
      if (res.data.ok) { toast.success(res.data.message); setSmtpStatus("active"); }
      else { toast.error(res.data.message); setSmtpStatus("problem"); }
    } catch { toast.error("Erro ao testar"); setSmtpStatus("problem"); }
    finally { setTesting(false); }
  };

  const handleToggle = async (val) => {
    const updated = { ...emailConfig, enabled: val };
    setEmailConfig(updated);
    await axios.put(`${API}/integrations/email`, updated, { withCredentials: true });
    if (val) checkSmtpStatus(updated);
    else setSmtpStatus("inactive");
  };

  const enabled = !!emailConfig?.enabled;

  const FIELDS = [
    { key: "host", label: "Servidor SMTP", placeholder: "" },
    { key: "port", label: "Porta", placeholder: "" },
    { key: "username", label: "Usuário (e-mail)", placeholder: "" },
    { key: "password", label: "Senha", secret: true },
    { key: "from_name", label: "Nome do remetente", placeholder: "" },
  ];

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <Mail className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">E-mail (SMTP)</p>
              <Badge className={!enabled ? "bg-slate-100 text-slate-500 border-0" : smtpStatus === "active" ? "bg-emerald-100 text-emerald-700 border-0" : smtpStatus === "problem" ? "bg-amber-100 text-amber-700 border-0" : "bg-rose-100 text-rose-700 border-0"}>
                {!enabled ? "Desconectado" : smtpStatus === "active" ? "Conectado" : smtpStatus === "problem" ? "Problema" : "Inativo"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Ative para utilizar envio de e-mails direto pelo sistema.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      {/* SMTP Config Modal — sidebar layout */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-2xl !p-0 overflow-hidden bg-white">
          <div className="flex h-[520px]">
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">E-mail SMTP</p>
              <div className="space-y-0.5">
                <button className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: tc500 }}>
                  <Key className="w-4 h-4" />Autenticação
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação E-mail SMTP
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Configure o servidor SMTP para envio de e-mails transacionais, campanhas e notificações do sistema.</p>
              </div>

              {enabled && smtpStatus === "active" ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                  <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                  {emailConfig?.config?.username && <p className="text-xs mt-1 text-emerald-600">{emailConfig.config.username}</p>}
                </div>
              ) : enabled && smtpStatus === "problem" ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <p className="font-semibold flex items-center gap-2"><AlertCircle className="w-4 h-4" />Problema na conexão</p>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  Não conectado. Preencha os dados SMTP e salve.
                </div>
              )}

              {enabled && (
                <div className="flex gap-2">
                  <Button onClick={testConn} disabled={testing} variant="outline" size="sm" className="text-xs gap-1.5">
                    {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}Verificar Conexão
                  </Button>
                  <Button onClick={() => handleToggle(false)} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                    <XCircle className="w-3 h-3" />Desconectar
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                {FIELDS.map(field => (
                  <div key={field.key}>
                    <Label className="text-sm">{field.label}</Label>
                    <div className="relative mt-1">
                      <Input
                        data-testid={`smtp-config-${field.key}`}
                        type={field.secret && !showSecrets[field.key] ? "password" : "text"}
                        value={emailConfig?.config?.[field.key] || ""}
                        onChange={e => setEmailConfig({ ...emailConfig, config: { ...emailConfig?.config, [field.key]: e.target.value } })}
                        placeholder={field.placeholder || ""}
                        className="pr-9"
                      />
                      {field.secret && (
                        <button type="button" onClick={() => setShowSecrets(p => ({ ...p, [field.key]: !p[field.key] }))}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button data-testid="smtp-config-save-btn" onClick={save} className="text-white" style={{ backgroundColor: tc500 }}>
                  <Check className="w-4 h-4 mr-1" />Salvar
                </Button>
                {!enabled && (
                  <Button onClick={() => handleToggle(true)} variant="outline" className="gap-1.5">
                    <Zap className="w-4 h-4" />Ativar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AISummaryCard({ tc500, tc50 }) {
  const [config, setConfig] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [configOpen, setConfigOpen] = useState(false);
  const [configTab, setConfigTab] = useState("auth");
  const [routine, setRoutine] = useState({ enabled: false, frequency_hours: 24, min_interval_hours: 24, status_filter: ["aberto"], max_per_run: 50, pipeline_ids: ["all"], stage_ids: ["all"] });
  const [routineLoading, setRoutineLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runningNow, setRunningNow] = useState(false);
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);
  const [cancellingRun, setCancellingRun] = useState(false);
  const [aiDisconnectConfirmOpen, setAiDisconnectConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [aiPipelines, setAiPipelines] = useState([]);
  const [aiStages, setAiStages] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    axios.get(`${API}/integrations/ai_summary`, { withCredentials: true })
      .then(res => setConfig(res.data))
      .catch(() => setConfig({ enabled: false, config: {} }))
      .finally(() => setInitialLoading(false));
  }, []);

  const loadRoutineData = async () => {
    try {
      const [cfgRes, runsRes, pipsRes] = await Promise.all([
        axios.get(`${API}/ai-routine/config`),
        axios.get(`${API}/ai-routine/runs`),
        axios.get(`${API}/pipelines`),
      ]);
      const cfg = cfgRes.data;
      if (typeof cfg.status_filter === "string") cfg.status_filter = [cfg.status_filter];
      if (!Array.isArray(cfg.status_filter)) cfg.status_filter = ["aberto"];
      if (!cfg.pipeline_ids) cfg.pipeline_ids = ["all"];
      if (!cfg.stage_ids) cfg.stage_ids = ["all"];
      setRoutine(cfg);
      setRuns(runsRes.data);
      setAiPipelines(pipsRes.data);
      // Load all stages for all pipelines
      const stagePromises = pipsRes.data.map(p =>
        axios.get(`${API}/pipeline-stages?pipeline_id=${p.id}`).then(r => r.data.map(s => ({ ...s, pipeline_name: p.name, pipeline_id: p.id })))
      );
      const allStages = (await Promise.all(stagePromises)).flat();
      setAiStages(allStages);
    } catch { /* silent */ }
  };

  const loadLogs = async (page = 0) => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/ai-analysis-logs`, { params: { skip: page * 20, limit: 20 }, withCredentials: true });
      setLogs(res.data.data || []);
      setLogsTotal(res.data.total || 0);
      setLogsPage(page);
    } catch { /* silent */ }
    finally { setLogsLoading(false); }
  };

  useEffect(() => {
    if (configOpen && configTab === "rotinas") loadRoutineData();
    if (configOpen && configTab === "historico") loadLogs(0);
  }, [configOpen, configTab]); // eslint-disable-line

  const enabled = !!config?.enabled;
  const hasRunning = runs.some(r => r.status === "running");

  // Poll progress while running
  useEffect(() => {
    if (!hasRunning && !runningNow) { setProgress(null); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/ai-routine/progress`);
        setProgress(res.data);
        if (res.data.status !== "running") {
          loadRoutineData();
        }
      } catch { /* silent */ }
    };
    poll();
    const interval = setInterval(poll, 2500);
    return () => clearInterval(interval);
  }, [hasRunning, runningNow]); // eslint-disable-line

  const handleToggle = async (val) => {
    setLoadingToggle(true);
    try {
      const updated = { ...config, enabled: val };
      await axios.put(`${API}/integrations/ai_summary`, updated, { withCredentials: true });
      setConfig(updated);
      toast.success(val ? "Resumo com IA ativado!" : "Resumo com IA desativado.");
    } catch {
      toast.error("Erro ao alterar configuração de IA");
    } finally { setLoadingToggle(false); }
  };

  const saveAuth = async () => {
    try {
      await axios.put(`${API}/integrations/ai_summary`, config, { withCredentials: true });
      toast.success("Configuração de IA salva!");
    } catch { toast.error("Erro ao salvar"); }
  };

  const saveRoutine = async () => {
    setRoutineLoading(true);
    try {
      await axios.put(`${API}/ai-routine/config`, routine, { withCredentials: true });
      toast.success("Rotina salva!");
    } catch { toast.error("Erro ao salvar rotina"); }
    finally { setRoutineLoading(false); }
  };

  const triggerRun = async () => {
    setConfirmRunOpen(false);
    setRunningNow(true);
    try {
      await axios.post(`${API}/ai-routine/run`, {}, { withCredentials: true });
      toast.success("Rotina iniciada em background!");
      setTimeout(loadRoutineData, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar rotina");
    } finally { setRunningNow(false); }
  };

  const cancelRun = async () => {
    setCancellingRun(true);
    try {
      await axios.post(`${API}/ai-routine/cancel`, {}, { withCredentials: true });
      toast.success("Cancelamento solicitado. A rotina será encerrada em breve.");
      setTimeout(loadRoutineData, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar rotina");
    } finally { setCancellingRun(false); }
  };

  const updateField = (key, val) => {
    setConfig(prev => ({ ...prev, config: { ...(prev?.config || {}), [key]: val } }));
  };

  const toggleStatus = (val) => {
    setRoutine(prev => {
      let current = Array.isArray(prev.status_filter) ? [...prev.status_filter] : [];
      if (val === "all") return { ...prev, status_filter: ["all"] };
      current = current.filter(v => v !== "all");
      if (current.includes(val)) {
        current = current.filter(v => v !== val);
      } else {
        current.push(val);
      }
      if (current.length === 0) current = ["aberto"];
      return { ...prev, status_filter: current };
    });
  };

  const AUTH_FIELDS = [
    { key: "api_key", label: "API Key da OpenAI", placeholder: "sk-proj-...", secret: true, tooltip: "Crie em platform.openai.com/api-keys" },
    { key: "assistant_id", label: "Assistant ID", placeholder: "asst_...", secret: false, tooltip: "Crie seu assistente em platform.openai.com/assistants e copie o ID" },
  ];

  const FREQ_OPTIONS = [
    { value: 6, label: "A cada 6 horas" },
    { value: 12, label: "A cada 12 horas" },
    { value: 24, label: "A cada 24 horas" },
    { value: 48, label: "A cada 48 horas" },
  ];

  const INTERVAL_OPTIONS = [
    { value: 12, label: "12 horas" },
    { value: 24, label: "24 horas" },
    { value: 48, label: "48 horas" },
    { value: 72, label: "72 horas" },
  ];

  const STATUS_OPTIONS = [
    { value: "all", label: "Todos" },
    { value: "aberto", label: "Em aberto" },
    { value: "ganho", label: "Ganho" },
    { value: "perdido", label: "Perdido" },
  ];

  const statusFilterLabel = () => {
    const sf = Array.isArray(routine.status_filter) ? routine.status_filter : ["aberto"];
    if (sf.includes("all")) return "Todos";
    return sf.map(v => STATUS_OPTIONS.find(o => o.value === v)?.label || v).join(", ");
  };

  const formatRunDate = (iso) => {
    if (!iso) return "\u2014";
    try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
  };

  const aiConfigured = !!(config?.config?.api_key && config?.config?.assistant_id);

  const TABS = [
    { id: "auth", label: "Autenticação", icon: <Key className="w-4 h-4" /> },
    ...(aiConfigured ? [
      { id: "rotinas", label: "Rotinas", icon: <RefreshCw className="w-4 h-4" /> },
      { id: "historico", label: "Histórico", icon: <History className="w-4 h-4" /> },
    ] : []),
  ];

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <Brain className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">OpenAI</p>
              <Badge className={enabled ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {enabled ? "Conectado" : "Desconectado"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Resumos executivos, transcrição de áudio e inteligência artificial.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      {/* AI Config Modal with Sidebar */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-5xl w-[calc(100%-2rem)] !p-0 overflow-hidden bg-white sm:!p-0">
          <div className="flex h-[85vh]">
            {/* Sidebar */}
            <div className="w-44 bg-slate-50 border-r border-slate-200 p-3 flex-shrink-0">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">OpenAI</p>
              <div className="space-y-1">
                {TABS.map(tab => {
                  const isDisabled = !enabled && (tab.id === "rotinas" || tab.id === "historico");
                  return (
                    <button key={tab.id}
                      data-testid={`ai-tab-${tab.id}`}
                      onClick={() => { if (!isDisabled) setConfigTab(tab.id); }}
                      disabled={isDisabled}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${configTab === tab.id ? "text-white shadow-sm font-medium" : isDisabled ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-white/60 hover:text-slate-700"}`}
                      style={configTab === tab.id ? { backgroundColor: tc500 } : undefined}>
                      {tab.icon}
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-5 overflow-y-auto">
              {configTab === "auth" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação OpenAI
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">Configure sua API Key e o ID do Assistente criado no painel da OpenAI.</p>
                  </div>

                  {enabled && config?.config?.api_key ? (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                      <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                      {config.config.assistant_id && <p className="text-xs mt-1 text-emerald-600">Assistant: {config.config.assistant_id.slice(0, 20)}...</p>}
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                      Não conectado. Preencha a API Key e o ID do Assistente.
                    </div>
                  )}

                  {enabled && (
                    <div className="flex gap-2">
                      <Button onClick={() => setAiDisconnectConfirmOpen(true)} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                        <XCircle className="w-3 h-3" />Desconectar
                      </Button>
                    </div>
                  )}

                  {AUTH_FIELDS.map(field => (
                    <div key={field.key}>
                      <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                        {field.label}
                        {field.tooltip && (
                          <Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400 cursor-help" /></TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">{field.tooltip}</TooltipContent></Tooltip>
                        )}
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          data-testid={`ai-config-${field.key}`}
                          type={field.secret && !showSecrets[field.key] ? "password" : "text"}
                          value={config?.config?.[field.key] || ""}
                          onChange={e => updateField(field.key, e.target.value)}
                          placeholder={field.placeholder || ""}
                          className="pr-9"
                        />
                        {field.secret && (
                          <button type="button" onClick={() => setShowSecrets(p => ({ ...p, [field.key]: !p[field.key] }))}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Button data-testid="ai-config-save-btn" onClick={saveAuth} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90">
                      <Check className="w-4 h-4 mr-1" />Salvar
                    </Button>
                    {!enabled && (
                      <Button onClick={() => handleToggle(true)} variant="outline" className="gap-1.5">
                        <Zap className="w-4 h-4" />Ativar
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {configTab === "rotinas" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Rotinas de Análise Automática</h3>
                    <p className="text-xs text-slate-500 mt-1">Configure a análise automática de negócios em background usando IA.</p>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Ativar rotina automática</p>
                      <p className="text-xs text-slate-400">A IA analisará automaticamente os negócios na frequência configurada</p>
                    </div>
                    <Switch checked={routine.enabled} onCheckedChange={async (v) => {
                      const updated = { ...routine, enabled: v };
                      setRoutine(updated);
                      try { await axios.put(`${API}/ai-routine/config`, updated, { withCredentials: true }); toast.success(v ? "Rotina ativada!" : "Rotina desativada."); } catch { toast.error("Erro ao salvar"); }
                    }} />
                  </div>

                  {routine.enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                            Frequência de execução
                            <Tooltip><TooltipTrigger asChild><AlertCircle className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs">Define de quanto em quanto tempo a rotina será executada automaticamente.</TooltipContent></Tooltip>
                          </Label>
                          <Select value={String(routine.frequency_hours)} onValueChange={(v) => setRoutine(p => ({ ...p, frequency_hours: Number(v) }))}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                            Intervalo por negócio
                            <Tooltip><TooltipTrigger asChild><AlertCircle className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs">Se o negócio já foi analisado dentro desse período, ele será ignorado nessa rodada.</TooltipContent></Tooltip>
                          </Label>
                          <Select value={String(routine.min_interval_hours)} onValueChange={(v) => setRoutine(p => ({ ...p, min_interval_hours: Number(v) }))}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {INTERVAL_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                            Status dos negócios
                            <Tooltip><TooltipTrigger asChild><AlertCircle className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs">Filtra quais negócios serão incluídos na análise automática pelo status.</TooltipContent></Tooltip>
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button data-testid="ai-status-multiselect" className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm mt-1 hover:bg-slate-50 transition-colors">
                                <span className="truncate text-slate-700">{statusFilterLabel()}</span>
                                <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-52 p-2" align="start">
                              {STATUS_OPTIONS.map(o => {
                                const sf = Array.isArray(routine.status_filter) ? routine.status_filter : [];
                                const checked = o.value === "all" ? sf.includes("all") : sf.includes(o.value);
                                return (
                                  <label key={o.value} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-colors">
                                    <Checkbox checked={checked} onCheckedChange={() => toggleStatus(o.value)} />
                                    <span className="text-sm text-slate-700">{o.label}</span>
                                  </label>
                                );
                              })}
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-slate-700 text-sm flex items-center gap-1.5">
                            Máximo por execução
                            <Tooltip><TooltipTrigger asChild><AlertCircle className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs">Limita quantos negócios serão analisados por rodada para controlar o consumo de API.</TooltipContent></Tooltip>
                          </Label>
                          <Input
                            type="number"
                            min={1}
                            max={200}
                            value={routine.max_per_run}
                            onChange={(e) => setRoutine(p => ({ ...p, max_per_run: Number(e.target.value) || 50 }))}
                            className="mt-1"
                          />
                        </div>
                      </div>

                      {/* Pipeline & Stage filter */}
                      <div>
                        <Label className="text-slate-700 text-sm">Pipelines</Label>
                        <div className="mt-1 border border-slate-200 rounded-lg p-2 max-h-28 overflow-y-auto space-y-1">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={(routine.pipeline_ids || []).includes("all")}
                              onChange={() => setRoutine(p => ({ ...p, pipeline_ids: ["all"], stage_ids: ["all"] }))}
                              className="rounded" style={{ accentColor: tc500 }} />
                            <span className="font-medium text-slate-700">Todas as pipelines</span>
                          </label>
                          {aiPipelines.map(pip => {
                            const pids = routine.pipeline_ids || [];
                            const isAll = pids.includes("all");
                            const checked = isAll || pids.includes(pip.id);
                            return (
                              <label key={pip.id} className="flex items-center gap-2 text-sm cursor-pointer pl-4">
                                <input type="checkbox" checked={checked}
                                  onChange={e => {
                                    let next;
                                    if (isAll) {
                                      // Switching from "all" to specific: select all except this one (uncheck) or just this one (check)
                                      next = e.target.checked ? [pip.id] : aiPipelines.filter(p => p.id !== pip.id).map(p => p.id);
                                    } else {
                                      next = [...pids];
                                      if (e.target.checked) next.push(pip.id); else next = next.filter(x => x !== pip.id);
                                    }
                                    if (next.length === 0 || next.length === aiPipelines.length) next = ["all"];
                                    setRoutine(p => ({ ...p, pipeline_ids: next, stage_ids: ["all"] }));
                                  }}
                                  className="rounded" style={{ accentColor: tc500 }} />
                                <span className="text-slate-600">{pip.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-700 text-sm">Etapas</Label>
                        <div className="mt-1 border border-slate-200 rounded-lg p-2 max-h-36 overflow-y-auto space-y-1">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={(routine.stage_ids || []).includes("all")}
                              onChange={() => setRoutine(p => ({ ...p, stage_ids: ["all"] }))}
                              className="rounded" style={{ accentColor: tc500 }} />
                            <span className="font-medium text-slate-700">Todas as etapas</span>
                          </label>
                          {(() => {
                            const pids = routine.pipeline_ids || ["all"];
                            const isAllPip = pids.includes("all");
                            const filteredStages = isAllPip ? aiStages : aiStages.filter(s => pids.includes(s.pipeline_id));
                            const grouped = {};
                            filteredStages.forEach(s => { if (!grouped[s.pipeline_name]) grouped[s.pipeline_name] = []; grouped[s.pipeline_name].push(s); });
                            const sids = routine.stage_ids || [];
                            const isAllStg = sids.includes("all");
                            return Object.entries(grouped).map(([pipName, stages]) => (
                              <div key={pipName} className="pl-2">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1 mb-0.5">{pipName}</p>
                                {stages.map(s => (
                                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer pl-2">
                                    <input type="checkbox" checked={isAllStg || sids.includes(s.id)}
                                      onChange={e => {
                                        let next;
                                        if (isAllStg) {
                                          next = e.target.checked ? [s.id] : filteredStages.filter(x => x.id !== s.id).map(x => x.id);
                                        } else {
                                          next = [...sids];
                                          if (e.target.checked) next.push(s.id); else next = next.filter(x => x !== s.id);
                                        }
                                        if (next.length === 0 || next.length === filteredStages.length) next = ["all"];
                                        setRoutine(p => ({ ...p, stage_ids: next }));
                                      }}
                                      className="rounded" style={{ accentColor: tc500 }} />
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || "#94a3b8" }} />
                                    <span className="text-slate-600">{s.name}</span>
                                  </label>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <Button data-testid="ai-run-now-btn" variant="outline" size="sm" onClick={() => setConfirmRunOpen(true)} disabled={runningNow || hasRunning} className="gap-2">
                            {runningNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                            Executar Agora
                          </Button>
                          {hasRunning && (
                            <Button data-testid="ai-cancel-run-btn" variant="outline" size="sm" onClick={cancelRun} disabled={cancellingRun} className="gap-2 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                              {cancellingRun ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                              Cancelar Execução
                            </Button>
                          )}
                        </div>
                        <Button data-testid="ai-save-routine-btn" size="sm" onClick={saveRoutine} disabled={routineLoading} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 gap-2">
                          {routineLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Salvar Rotina
                        </Button>
                      </div>

                      {/* Real-time progress log */}
                      {progress && progress.status === "running" && (
                        <div className="space-y-2 p-3 rounded-lg border border-blue-200 bg-blue-50/50">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-700">Progresso</span>
                            <span className="text-xs font-mono text-blue-600">{progress.total_analyzed}/{progress.total_eligible}</span>
                          </div>
                          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress.total_eligible ? (progress.total_analyzed / progress.total_eligible) * 100 : 0}%`, backgroundColor: tc500 }} />
                          </div>
                          {progress.progress_log && progress.progress_log.length > 0 && (
                            <div className="max-h-40 overflow-y-auto space-y-1 mt-1" ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
                              {progress.progress_log.map((log, i) => (
                                <div key={i} className="flex items-center gap-2 text-[11px] overflow-hidden">
                                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${log.status === "ok" ? "bg-emerald-500" : "bg-rose-500"}`} />
                                  <span className="text-slate-700 truncate">{log.titulo}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {progress && progress.status !== "running" && progress.status !== "idle" && progress.progress_log?.length > 0 && (
                        <div className="space-y-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-700">Última execução: {progress.status === "completed" ? "Concluída" : "Cancelada"}</span>
                            <span className="text-xs font-mono text-slate-500">{progress.total_analyzed}/{progress.total_eligible} ({progress.total_errors} erros)</span>
                          </div>
                        </div>
                      )}

                      {runs.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-semibold text-slate-700">Últimas Execuções</h4>
                          <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {runs.map((r, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.status === "completed" ? "bg-emerald-500" : r.status === "running" ? "bg-amber-500 animate-pulse" : r.status === "cancelled" ? "bg-slate-400" : "bg-rose-500"}`} />
                                  <span className="text-slate-600">{formatRunDate(r.started_at)}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-slate-500">{r.total_analyzed || 0}/{r.total_eligible || 0} analisados</span>
                                  {r.status === "cancelled" && <span className="text-slate-400">Cancelada</span>}
                                  {r.total_errors > 0 && <span className="text-rose-500">{r.total_errors} erros</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {configTab === "historico" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Histórico de Análises IA</h3>
                    <p className="text-xs text-slate-500 mt-1">Todas as análises (manuais e automáticas) realizadas pela IA.</p>
                  </div>

                  {logsLoading && logs.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-12">
                      <History className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Nenhuma análise realizada ainda.</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                        {logs.map((log, i) => {
                          const isExpanded = expandedLog === i;
                          const isManual = log.triggered_by === "manual";
                          return (
                            <div key={i} className="rounded-lg border border-slate-100 bg-slate-50/50 overflow-hidden">
                              <button
                                data-testid={`ai-log-item-${i}`}
                                onClick={() => setExpandedLog(isExpanded ? null : i)}
                                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 transition-colors"
                              >
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === "success" ? "bg-emerald-500" : "bg-rose-500"}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-medium text-slate-700 truncate max-w-[180px]">{log.negocio_titulo || "Negócio"}</span>
                                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isManual ? "border-blue-200 text-blue-600 bg-blue-50" : "border-amber-200 text-amber-600 bg-amber-50"}`}>
                                      {isManual ? <User className="w-2.5 h-2.5 mr-0.5" /> : <Bot className="w-2.5 h-2.5 mr-0.5" />}
                                      {isManual ? "Manual" : "Rotina"}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {formatRunDate(log.created_at)}
                                    </span>
                                    {log.user_name && (
                                      <span className="text-[11px] text-slate-400">{log.user_name}</span>
                                    )}
                                    {log.duration_ms > 0 && (
                                      <span className="text-[11px] text-slate-400">{(log.duration_ms / 1000).toFixed(1)}s</span>
                                    )}
                                  </div>
                                </div>
                                {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                              </button>

                              {isExpanded && (
                                <div className="border-t border-slate-100 p-3 space-y-3 bg-white">
                                  {log.parsed_result && (
                                    <div className="space-y-2">
                                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Resultado</p>
                                      <div className="grid grid-cols-2 gap-2">
                                        {log.parsed_result.situacao && (
                                          <div className="text-xs"><span className="text-slate-400">Situação:</span> <span className="font-medium text-slate-700">{log.parsed_result.situacao}</span></div>
                                        )}
                                        {log.parsed_result.qualidade_do_lead && (
                                          <div className="text-xs"><span className="text-slate-400">Qualidade:</span> <span className="font-medium text-slate-700">{log.parsed_result.qualidade_do_lead}</span></div>
                                        )}
                                      </div>
                                      {log.parsed_result.resumo && (
                                        <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 rounded p-2">{log.parsed_result.resumo}</p>
                                      )}
                                      {log.parsed_result.pontos_de_atencao && (
                                        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">{log.parsed_result.pontos_de_atencao}</p>
                                      )}
                                    </div>
                                  )}

                                  {log.prompt && (
                                    <details className="group">
                                      <summary className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer flex items-center gap-1 hover:text-slate-600">
                                        <FileText className="w-3 h-3" /> Prompt enviado
                                      </summary>
                                      <pre className="mt-1 text-[11px] text-slate-500 bg-slate-50 rounded p-2 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">{log.prompt.slice(0, 2000)}{log.prompt.length > 2000 ? "..." : ""}</pre>
                                    </details>
                                  )}

                                  {log.response_raw && (
                                    <details className="group">
                                      <summary className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer flex items-center gap-1 hover:text-slate-600">
                                        <Bot className="w-3 h-3" /> Resposta bruta da OpenAI
                                      </summary>
                                      <pre className="mt-1 text-[11px] text-slate-500 bg-slate-50 rounded p-2 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">{log.response_raw.slice(0, 2000)}{log.response_raw.length > 2000 ? "..." : ""}</pre>
                                    </details>
                                  )}

                                  {log.error_msg && log.error_msg !== "" && (
                                    <div className="text-xs text-rose-600 bg-rose-50 rounded p-2">
                                      <span className="font-medium">Erro:</span> {log.error_msg}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {logsTotal > 20 && (
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-xs text-slate-400">{logsTotal} registros</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={logsPage === 0 || logsLoading} onClick={() => loadLogs(logsPage - 1)} className="h-7 text-xs px-2">Anterior</Button>
                            <span className="text-xs text-slate-500">Pág. {logsPage + 1}/{Math.ceil(logsTotal / 20)}</span>
                            <Button variant="outline" size="sm" disabled={(logsPage + 1) * 20 >= logsTotal || logsLoading} onClick={() => loadLogs(logsPage + 1)} className="h-7 text-xs px-2">Próxima</Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for manual run */}
      <AlertDialog open={confirmRunOpen} onOpenChange={setConfirmRunOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" style={{ color: tc500 }} />
              Executar Rotina Agora?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500">
              Isso iniciará a análise de IA para os negócios elegíveis em background. Cada análise consome créditos da sua API OpenAI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={triggerRun} style={{ backgroundColor: tc500 }} className="text-white">
              <Play className="w-4 h-4 mr-2" />Sim, executar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={aiDisconnectConfirmOpen} onOpenChange={setAiDisconnectConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
              <XCircle className="w-5 h-5" />Desconectar OpenAI?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500">
              Ao desconectar, as rotinas automáticas de análise, resumos executivos, transcrição de áudio e validação de documentos por IA deixarão de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => { handleToggle(false); setAiDisconnectConfirmOpen(false); }}>
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AutentiqueCard({ tc500, tc50 }) {
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [tab, setTab] = useState("auth");
  const [form, setForm] = useState({ api_key: "" });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [variables, setVariables] = useState([]);
  const [aiVariables, setAiVariables] = useState([]);

  const [connected, setConnected] = useState(false);

  const fetchConfig = useCallback(() => {
    axios.get(`${API}/integrations/autentique`).then(r => {
      setConfig(r.data);
      setConnected(r.data?.enabled && !!r.data?.config?.api_key);
      if (r.data?.config) setForm(f => ({ ...f, ...r.data.config }));
    }).catch(() => setConfig({ enabled: false }));
  }, []);
  const fetchTemplates = useCallback(() => {
    axios.get(`${API}/autentique/templates`).then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchConfig(); fetchTemplates(); }, [fetchConfig, fetchTemplates]);
  useEffect(() => {
    if (configOpen && tab === "contracts") {
      axios.get(`${API}/autentique/variables`).then(r => { setVariables(r.data?.fixed || []); setAiVariables(r.data?.ai || []); }).catch(() => {});
    }
  }, [configOpen, tab]);

  const saveAuth = async () => {
    setSaving(true);
    try {
      // Test the key first by listing documents
      const testResp = await axios.post(`${API}/autentique/test-connection`, { api_key: form.api_key });
      if (!testResp.data.ok) {
        toast.error(testResp.data.error || "Chave inválida");
        setSaving(false);
        return;
      }
      await axios.put(`${API}/integrations/autentique`, { enabled: true, config: form });
      setConnected(true);
      toast.success("Autentique conectado!"); fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chave inválida ou erro de conexão");
      setConnected(false);
    }
    setSaving(false);
  };

  const saveTemplate = async () => {
    setTemplateSaving(true);
    try {
      if (editTemplate.id) {
        await axios.put(`${API}/autentique/templates/${editTemplate.id}`, editTemplate);
      } else {
        await axios.post(`${API}/autentique/templates`, editTemplate);
      }
      toast.success("Modelo salvo!"); fetchTemplates(); setEditTemplate(null);
    } catch { toast.error("Erro"); }
    setTemplateSaving(false);
  };

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <FileText className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">Autentique</p>
              <Badge className={connected ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {connected ? "Conectado" : "Desconectado"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Assinatura digital de contratos.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen} modal={!editorOpen}>
        <DialogContent className="max-w-5xl !p-0 overflow-hidden bg-white" onInteractOutside={e => { if (editorOpen) e.preventDefault(); }}>
          <div className="flex h-[85vh]">
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">Autentique</p>
              {[{ id: "auth", label: "Autenticação", icon: <Key className="w-4 h-4" /> },
                { id: "contracts", label: "Contratos", icon: <FileText className="w-4 h-4" /> },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium transition-colors mb-0.5 ${tab === t.id ? "text-white" : "text-slate-500 hover:bg-white/60"}`}
                  style={tab === t.id ? { backgroundColor: tc500 } : {}}>
                  {t.icon}{t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
              {tab === "auth" ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação Autentique
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">Plataforma de assinatura digital de contratos. Gere sua API Key em painel.autentique.com.br.</p>
                  </div>

                  {connected ? (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                      <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                      Não conectado. Insira sua API Key do Autentique.
                    </div>
                  )}

                  {connected && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={async () => {
                        try {
                          await axios.put(`${API}/integrations/autentique`, { enabled: false, config: { api_key: "" } });
                          setConnected(false);
                          setForm({ api_key: "" });
                          toast.success("Autentique desconectado!");
                          fetchConfig();
                        } catch { toast.error("Erro"); }
                      }}>
                        <XCircle className="w-3 h-3" />Desconectar
                      </Button>
                    </div>
                  )}

                  <p className="text-xs text-slate-500">Configure sua API Key gerada no painel da Autentique.</p>
                  <div>
                    <Label className="text-slate-700 text-sm">API Key</Label>
                    <Input value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} type="password" placeholder="Bearer token" className="mt-1" />
                    <p className="text-xs text-slate-400 mt-1">Gere em painel.autentique.com.br → Perfil → API Keys</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveAuth} disabled={saving} className="text-white" style={{ backgroundColor: tc500 }}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Salvar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: tc500 }} />Modelos de Contrato</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">Crie e gerencie templates de contrato com variáveis dinâmicas, signatários e regras de pipeline.</p>
                    </div>
                    <Button size="sm" className="text-xs gap-1 text-white" style={{ backgroundColor: tc500 }} onClick={() => { setEditTemplate({ name: "", html_body: "", signers: [{ email: "{email_contato}", action: "SIGN" }], options: {} }); setEditorOpen(true); }}>
                      <Plus className="w-3 h-3" />Novo modelo
                    </Button>
                  </div>
                  {templates.length === 0 && !editTemplate && <p className="text-sm text-slate-400 text-center py-6">Nenhum modelo de contrato criado.</p>}
                  {!editTemplate && (
                    <div className="space-y-2">
                      {templates.map(t => (
                        <div key={t.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                          <FileText className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setEditTemplate(t); setEditorOpen(true); }}>
                            <p className="text-sm font-medium text-slate-800">{t.name}</p>
                            <p className="text-[10px] text-slate-400">{t.signers?.length || 0} signatário(s) · {new Date(t.created_at).toLocaleDateString("pt-BR")}</p>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditTemplate(t); setEditorOpen(true); }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={async () => { await axios.delete(`${API}/autentique/templates/${t.id}`); fetchTemplates(); toast.success("Removido"); }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Fullscreen Editor */}
                  <ContractTemplateEditor
                    open={editorOpen}
                    onClose={() => { setEditorOpen(false); setEditTemplate(null); fetchTemplates(); }}
                    template={editTemplate}
                    aiVariables={aiVariables}
                    onSave={async () => {
                      fetchTemplates();
                      setEditorOpen(false);
                      setEditTemplate(null);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AWSS3Card({ tc500, tc50 }) {
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ bucket_name: "", region: "us-east-1", access_key: "", secret_key: "" });
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const fetchConfig = useCallback(() => {
    axios.get(`${API}/integrations/aws-s3`).then(r => {
      setConfig(r.data);
      if (r.data?.config) setForm(f => ({
        ...f,
        bucket_name: r.data.config.bucket_name || r.data.config.bucket || "",
        region: r.data.config.region || "us-east-1",
        access_key: f.access_key || "",
        secret_key: f.secret_key || "",
      }));
    }).catch(() => setConfig({ enabled: false }));
  }, []);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const save = async () => {
    if (!form.bucket_name || !form.access_key || !form.secret_key) {
      toast.error("Preencha todos os campos.");
      return;
    }
    setSaving(true);
    try {
      // Test connection first
      const testRes = await axios.post(`${API}/files/test-s3`, {
        bucket_name: form.bucket_name,
        region: form.region,
        access_key: form.access_key === "••••••••" ? undefined : form.access_key,
        secret_key: form.secret_key === "••••••••" ? undefined : form.secret_key,
      });
      if (!testRes.data.ok) {
        toast.error(testRes.data.error || "Falha na conexão com o S3. Verifique as credenciais.");
        setSaving(false);
        return;
      }
      await axios.put(`${API}/integrations/aws-s3`, { enabled: true, config: form });
      toast.success("AWS S3 conectado com sucesso!");
      fetchConfig();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    try {
      await axios.put(`${API}/integrations/aws-s3`, { config: {}, enabled: false });
      setConfig({ enabled: false });
      setDisconnectOpen(false);
      setForm({ bucket_name: "", region: "us-east-1", access_key: "", secret_key: "" });
      toast.success("AWS S3 desconectado.");
    } catch { toast.error("Erro"); }
  };

  const connected = config?.enabled && (config?.config?.bucket_name || config?.config?.bucket);

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <FolderOpen className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">AWS S3</p>
              <Badge className={connected ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {connected ? "Conectado" : "Desconectado"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Armazenamento de arquivos e documentos.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-2xl !p-0 overflow-hidden bg-white">
          <div className="flex h-[520px]">
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">AWS S3</p>
              <div className="space-y-0.5">
                <button className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: tc500 }}>
                  <Key className="w-4 h-4" />Autenticação
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação AWS S3
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Armazenamento de arquivos e documentos na nuvem. Configure as credenciais do seu bucket S3.</p>
              </div>
              {connected ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                  <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                  <p className="text-xs mt-1 text-emerald-600">Bucket: {config.config.bucket_name || config.config.bucket}</p>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  Não conectado. Preencha as credenciais do seu bucket S3.
                </div>
              )}
              {connected && (
                <Button onClick={() => setDisconnectOpen(true)} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                  <XCircle className="w-3 h-3" />Desconectar
                </Button>
              )}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Nome do Bucket</Label>
                  <Input value={form.bucket_name} onChange={e => setForm(f => ({ ...f, bucket_name: e.target.value }))} placeholder="meu-bucket" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Região</Label>
                  <Input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} placeholder="us-east-1" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Access Key ID</Label>
                  <Input value={form.access_key} onChange={e => setForm(f => ({ ...f, access_key: e.target.value }))} placeholder="AKIA..." className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Secret Access Key</Label>
                  <Input value={form.secret_key} onChange={e => setForm(f => ({ ...f, secret_key: e.target.value }))} type="password" placeholder="wJalr..." className="mt-1" />
                </div>
              </div>
              <Button onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: tc500 }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><XCircle className="w-5 h-5" />Desconectar AWS S3?</AlertDialogTitle>
            <AlertDialogDescription>Os uploads de arquivos deixarão de funcionar até que uma nova conexão seja configurada.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDisconnect}>Desconectar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function GoToConnectCard({ tc500, tc50 }) {
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", client_secret: "", extension: "" });
  const [saving, setSaving] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [loadingExts, setLoadingExts] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchConfig = useCallback(() => {
    axios.get(`${API}/goto/config`).then(r => {
      setConfig(r.data);
      if (r.data.client_id) setForm(f => ({ ...f, client_id: r.data.client_id }));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const loadExtensions = async () => {
    setLoadingExts(true);
    try {
      const r = await axios.get(`${API}/goto/extensions`);
      setExtensions(r.data);
    } catch { toast.error("Erro ao buscar ramais"); }
    setLoadingExts(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/goto/config`, form);
      toast.success("Configuração salva!");
      fetchConfig();
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const authorize = async () => {
    try {
      const r = await axios.get(`${API}/goto/authorize-url?redirect_origin=${encodeURIComponent(window.location.origin)}`, { withCredentials: true });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Erro ao iniciar conexão");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao conectar GoTo");
    }
  };

  const verifyConnection = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await axios.post(`${API}/goto/verify`);
      setVerifyResult(r.data);
      if (r.data.connected) {
        toast.success(`Conexão válida! ${r.data.name || ""}`);
      } else {
        toast.error(r.data.error || "Token inválido");
      }
    } catch (err) {
      toast.error("Erro ao verificar");
      setVerifyResult({ connected: false, error: "Erro na requisição" });
    }
    setVerifying(false);
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API}/goto/disconnect`);
      toast.success("GoTo Connect desconectado!");
      setVerifyResult(null);
      fetchConfig();
    } catch { toast.error("Erro ao desconectar"); }
    setDisconnecting(false);
  };

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Phone className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900">GoTo Connect</p>
              <Badge className={config?.connected ? "bg-emerald-100 text-emerald-700 border-0" : config?.token_expired ? "bg-amber-100 text-amber-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {config?.connected ? "Conectado" : config?.token_expired ? "Token Expirado" : "Desconectado"}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400 font-normal">Telefonia integrada — faça e receba ligações pelo sistema.</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-2xl !p-0 overflow-hidden bg-white">
          <div className="flex h-[520px]">
            {/* Sidebar */}
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">GoTo Connect</p>
              <div className="space-y-0.5">
                <button className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: tc500 }}>
                  <Key className="w-4 h-4" />Autenticação
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação GoTo Connect
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Telefonia integrada via GoTo Connect. Faça e receba ligações diretamente pelo sistema com gravação e transcrição automática.</p>
              </div>

              {config?.connected ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                  <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                  {config.principal && <p className="text-xs mt-1 text-emerald-600">{config.principal}</p>}
                </div>
              ) : config?.has_token && config?.token_expired ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <p className="font-semibold flex items-center gap-2"><AlertCircle className="w-4 h-4" />Token expirado</p>
                  <p className="text-xs mt-1">Desconecte e reconecte para renovar o token OAuth.</p>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  Não conectado. Configure as credenciais e clique em "Conectar GoTo".
                </div>
              )}

              {/* Verify / Disconnect */}
              {config?.has_token && (
                <div className="flex gap-2">
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="outline" size="sm" className="text-xs px-2" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/goto/callback`);
                      toast.success("URL de callback copiada!");
                    }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Copiar URL de Callback</TooltipContent></Tooltip>
                  <Button onClick={verifyConnection} disabled={verifying} variant="outline" size="sm" className="text-xs gap-1.5">
                    {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Verificar Conexão
                  </Button>
                  <Button onClick={disconnect} disabled={disconnecting} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                    {disconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    Desconectar
                  </Button>
                </div>
              )}
              {verifyResult && !verifyResult.connected && (
                <div className="p-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
                  {verifyResult.error}
                </div>
              )}
              {verifyResult?.connected && (
                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                  Token válido! {verifyResult.name} — {verifyResult.email}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Client ID</Label>
                  <Input value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} placeholder="2919d0ca-..." className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Client Secret</Label>
                  <Input value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} type="password" placeholder="tCR5P69..." className="mt-1" />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={saveConfig} disabled={saving} className="text-white" style={{ backgroundColor: tc500 }}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Salvar
                </Button>
                {form.client_id && (
                  <Button onClick={authorize} variant="outline" className="gap-1.5">
                    <ExternalLink className="w-4 h-4" />Conectar GoTo
                  </Button>
                )}
              </div>

              {/* Extensions list */}
              {config?.connected && (
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Ramais disponíveis</Label>
                    <Button variant="outline" size="sm" onClick={loadExtensions} disabled={loadingExts} className="text-xs">
                      {loadingExts ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}Carregar
                    </Button>
                  </div>
                  {extensions.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100">
                      {extensions.map(ext => (
                        <div key={ext.extension} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-mono font-semibold text-slate-700">{ext.extension}</span>
                          </div>
                          <span className="text-xs text-slate-500">{ext.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Clique em "Carregar" para ver os ramais.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


function AsaasCard({ tc500, tc50 }) {
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [tab, setTab] = useState("auth");
  const [form, setForm] = useState({ api_key: "", environment: "sandbox" });
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [billingConfig, setBillingConfig] = useState({ billing_types: ["BOLETO", "PIX", "CREDIT_CARD"], max_installments: 12 });
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [stagesMap, setStagesMap] = useState({});
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState(null);

  const fetchConfig = useCallback(() => {
    axios.get(`${API}/integrations/asaas`).then(r => {
      setConfig(r.data);
      setConnected(r.data?.enabled && !!r.data?.config?.api_key);
      if (r.data?.config) {
        setForm(f => ({ ...f, environment: r.data.config.environment || "sandbox", api_key: f.api_key || "" }));
        if (r.data.config.billing_types) setBillingConfig(bc => ({ ...bc, billing_types: r.data.config.billing_types }));
        if (r.data.config.max_installments) setBillingConfig(bc => ({ ...bc, max_installments: r.data.config.max_installments }));
      }
    }).catch(() => {});
  }, []);
  const fetchTemplates = useCallback(() => {
    axios.get(`${API}/asaas/billing-templates`).then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchConfig(); fetchTemplates(); }, [fetchConfig, fetchTemplates]);
  useEffect(() => {
    if (configOpen) {
      axios.get(`${API}/pipelines`).then(r => {
        const pipes = r.data || [];
        setPipelines(pipes);
        pipes.forEach(p => {
          axios.get(`${API}/pipeline-stages?pipeline_id=${p.id}`).then(sr => {
            setStagesMap(prev => ({ ...prev, [p.id]: sr.data || [] }));
          }).catch(() => {});
        });
      }).catch(() => {});
    }
  }, [configOpen]);

  const saveAuth = async () => {
    setSaving(true);
    try {
      // Test connection first
      if (form.api_key && form.api_key !== "••••••••") {
        const test = await axios.post(`${API}/asaas/test-connection`, { api_key: form.api_key, environment: form.environment || "sandbox" });
        if (!test.data.ok) {
          toast.error(test.data.error || "API Key inválida. Verifique e tente novamente.");
          setSaving(false);
          return;
        }
      }
      await axios.put(`${API}/integrations/asaas`, { config: { ...form, api_key: form.api_key || "••••••••", billing_types: billingConfig.billing_types, max_installments: billingConfig.max_installments }, enabled: true });
      toast.success("Conectado com sucesso!");
      setConnected(true);
      fetchConfig();
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    try {
      await axios.put(`${API}/integrations/asaas`, { config: {}, enabled: false });
      setConnected(false);
      setDisconnectOpen(false);
      fetchConfig();
      toast.success("Asaas desconectado.");
    } catch { toast.error("Erro"); }
  };

  const saveTemplate = async (tpl) => {
    try {
      if (tpl.id) await axios.put(`${API}/asaas/billing-templates/${tpl.id}`, tpl);
      else await axios.post(`${API}/asaas/billing-templates`, tpl);
      fetchTemplates();
      setEditTemplate(null);
      toast.success("Modelo salvo!");
    } catch { toast.error("Erro"); }
  };

  const BILLING_TYPES = [
    { value: "BOLETO", label: "Boleto" },
    { value: "PIX", label: "Pix" },
    { value: "CREDIT_CARD", label: "Cartão de Crédito" },
  ];

  return (
    <>
      <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setConfigOpen(true)}>
        <CardHeader className="flex flex-row items-center gap-4 py-4 px-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
            <Receipt className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
              Asaas
              <Badge className={connected ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                {connected ? "Conectado" : "Desconectado"}
              </Badge>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">Cobranças · Boleto · Pix · Cartão</p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setConfigOpen(true); }}>Configurar</Button>
        </CardHeader>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-4xl !p-0 !gap-0 !max-h-none !overflow-hidden bg-white" style={{ height: "80vh" }}>
          <div className="flex h-full overflow-hidden">
            <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2 overflow-y-auto">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">Asaas</p>
              {[{ id: "auth", label: "Autenticação", icon: <Key className="w-4 h-4" /> },
                { id: "templates", label: "Modelos", icon: <Receipt className="w-4 h-4" /> },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium transition-colors mb-0.5 ${tab === t.id ? "text-white" : "text-slate-500 hover:bg-white/60"}`}
                  style={tab === t.id ? { backgroundColor: tc500 } : {}}>
                  {t.icon}{t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 p-6 space-y-4 overflow-y-auto">
              {tab === "auth" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Key className="w-4 h-4" style={{ color: tc500 }} />Autenticação Asaas
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">Plataforma de cobranças. Gere sua API Key em sandbox.asaas.com ou app.asaas.com.</p>
                  </div>
                  {connected ? (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                      <p className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" />Conectado</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                      Não conectado. Insira sua API Key do Asaas.
                    </div>
                  )}
                  {connected && (
                    <Button onClick={() => setDisconnectOpen(true)} variant="outline" size="sm" className="text-xs gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50">
                      <XCircle className="w-3 h-3" />Desconectar
                    </Button>
                  )}
                  <div>
                    <Label className="text-sm text-slate-700">API Key</Label>
                    <Input type="password" value={form.api_key || ""} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} placeholder="$aact_..." className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">Ambiente</Label>
                    <Select value={form.environment || "sandbox"} onValueChange={v => setForm(f => ({ ...f, environment: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Testes)</SelectItem>
                        <SelectItem value="production">Produção</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={saveAuth} disabled={saving} className="text-white" style={{ backgroundColor: tc500 }}>
                    <Check className="w-4 h-4 mr-1" />Salvar
                  </Button>
                </div>
              )}

              {tab === "templates" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4" style={{ color: tc500 }} />Modelos de Cobrança</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">Crie modelos com valores padrão e regras de pipeline para cobranças.</p>
                    </div>
                    <Button size="sm" className="text-xs gap-1 text-white" style={{ backgroundColor: tc500 }} onClick={() => setEditTemplate({ name: "", billing_type: "BOLETO", default_value: 0, default_due_days: 7, default_description: "", billing_types: ["BOLETO", "PIX", "CREDIT_CARD"], max_installments: 12, pipeline_stages: [], billing_rule: "none" })}>
                      <Plus className="w-3 h-3" />Novo modelo
                    </Button>
                  </div>
                  {templates.length === 0 && !editTemplate && <p className="text-sm text-slate-400 text-center py-6">Nenhum modelo de cobrança criado.</p>}
                  {!editTemplate && templates.map(tpl => (
                    <div key={tpl.id} onClick={() => setEditTemplate(tpl)} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors cursor-pointer">
                      <Receipt className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{tpl.name}</p>
                        <p className="text-[10px] text-slate-400">{tpl.billing_type} · R$ {(tpl.default_value || 0).toFixed(2)} · {tpl.default_due_days}d vencimento</p>
                      </div>
                      <button onClick={e => { e.stopPropagation(); setDeleteTemplateTarget(tpl); }} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {editTemplate && (
                    <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                      <Input value={editTemplate.name} onChange={e => setEditTemplate(t => ({ ...t, name: e.target.value }))} placeholder="Nome do modelo" />
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Tipo padrão</Label>
                          <Select value={editTemplate.billing_type} onValueChange={v => setEditTemplate(t => ({ ...t, billing_type: v }))}>
                            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{BILLING_TYPES.map(bt => <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Origem do valor</Label>
                          <Select value={editTemplate.value_source || "fixed"} onValueChange={v => setEditTemplate(t => ({ ...t, value_source: v }))}>
                            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Valor fixo</SelectItem>
                              <SelectItem value="valor_negocio">Valor do Negócio</SelectItem>
                              <SelectItem value="valor_mrr">Valor MRR</SelectItem>
                              <SelectItem value="valor_setup">Valor Setup</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {(editTemplate.value_source || "fixed") === "fixed" && (
                        <div>
                          <Label className="text-xs text-slate-500">Valor padrão (R$)</Label>
                          <Input type="number" value={editTemplate.default_value} onChange={e => setEditTemplate(t => ({ ...t, default_value: parseFloat(e.target.value) || 0 }))} className="mt-1 h-9 text-sm" />
                        </div>
                        )}
                        <div>
                          <Label className="text-xs text-slate-500">Dias para vencimento</Label>
                          <Input type="number" value={editTemplate.default_due_days} onChange={e => setEditTemplate(t => ({ ...t, default_due_days: parseInt(e.target.value) || 7 }))} className="mt-1 h-9 text-sm" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Descrição padrão</Label>
                        <Input value={editTemplate.default_description} onChange={e => setEditTemplate(t => ({ ...t, default_description: e.target.value }))} placeholder="Ex: Mensalidade do serviço" className="mt-1" />
                      </div>

                      {/* Billing types + installments */}
                      <div className="pt-2 border-t border-slate-100 space-y-3">
                        <p className="text-xs font-semibold text-slate-700">Formas de pagamento</p>
                        <div className="space-y-1.5">
                          {BILLING_TYPES.map(bt => (
                            <label key={bt.value} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox checked={(editTemplate.billing_types || ["BOLETO", "PIX", "CREDIT_CARD"]).includes(bt.value)} onCheckedChange={v => {
                                setEditTemplate(t => {
                                  const current = t.billing_types || ["BOLETO", "PIX", "CREDIT_CARD"];
                                  return { ...t, billing_types: v ? [...current, bt.value] : current.filter(x => x !== bt.value) };
                                });
                              }} />
                              <span className="text-sm text-slate-600">{bt.label}</span>
                            </label>
                          ))}
                        </div>
                        {(editTemplate.billing_types || ["BOLETO", "PIX", "CREDIT_CARD"]).includes("CREDIT_CARD") && (
                          <div>
                            <Label className="text-xs text-slate-500">Máximo de parcelas</Label>
                            <Select value={String(editTemplate.max_installments || 12)} onValueChange={v => setEditTemplate(t => ({ ...t, max_installments: parseInt(v) }))}>
                              <SelectTrigger className="mt-1 w-32 h-9 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <SelectItem key={n} value={String(n)}>{n}x</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        <label className="flex items-center gap-2 cursor-pointer pt-1">
                          <Checkbox checked={editTemplate.pass_customer_fees || false} onCheckedChange={v => setEditTemplate(t => ({ ...t, pass_customer_fees: !!v }))} />
                          <span className="text-sm text-slate-600">Repassar taxas ao cliente</span>
                        </label>
                      </div>

                      {/* Pipeline rules */}
                      <div className="pt-2 border-t border-slate-100 space-y-3">
                        <p className="text-xs font-semibold text-slate-700">Regras de Pipeline</p>
                        <p className="text-[10px] text-slate-400">Selecione em quais etapas a aba Cobranças deve aparecer.</p>
                        {pipelines.map(pipe => {
                          const stages = stagesMap[pipe.id] || [];
                          const existing = (editTemplate.pipeline_stages || []).find(ps => ps.pipeline_id === pipe.id);
                          const selectedIds = existing?.stage_ids || [];
                          const toggleStage = (stageId) => {
                            setEditTemplate(t => {
                              const ps = [...(t.pipeline_stages || [])];
                              const idx = ps.findIndex(p => p.pipeline_id === pipe.id);
                              if (idx === -1) return { ...t, pipeline_stages: [...ps, { pipeline_id: pipe.id, stage_ids: [stageId] }] };
                              const sids = ps[idx].stage_ids || [];
                              ps[idx] = { ...ps[idx], stage_ids: sids.includes(stageId) ? sids.filter(s => s !== stageId) : [...sids, stageId] };
                              if (ps[idx].stage_ids.length === 0) return { ...t, pipeline_stages: ps.filter((_, i) => i !== idx) };
                              return { ...t, pipeline_stages: ps };
                            });
                          };
                          return (
                            <div key={pipe.id} className="border border-slate-200 rounded-lg p-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-600">{pipe.name}</p>
                              <div className="flex flex-wrap gap-1">
                                {stages.sort((a, b) => (a.order || 0) - (b.order || 0)).map(st => (
                                  <button key={st.id} onClick={() => toggleStage(st.id)}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${selectedIds.includes(st.id) ? "text-white" : "border-slate-200 text-slate-500"}`}
                                    style={selectedIds.includes(st.id) ? { backgroundColor: tc500, borderColor: tc500 } : {}}>
                                    {selectedIds.includes(st.id) && <Check className="w-2.5 h-2.5 inline mr-0.5" />}{st.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        <div className="space-y-1.5">
                          {[
                            { value: "none", label: "Sem regra", desc: "Apenas exibe a aba Cobranças" },
                            { value: "emitida", label: "Obrigatório cobrança emitida", desc: "Bloqueia avanço/ganho sem cobrança emitida" },
                            { value: "liquidada", label: "Obrigatório cobrança liquidada", desc: "Bloqueia avanço/ganho sem cobrança liquidada" },
                          ].map(opt => (
                            <button key={opt.value} onClick={() => setEditTemplate(t => ({ ...t, billing_rule: opt.value }))}
                              className={`w-full text-left p-2 rounded-lg border transition-colors ${editTemplate.billing_rule === opt.value ? "" : "border-slate-200 hover:border-slate-300"}`}
                              style={editTemplate.billing_rule === opt.value ? { backgroundColor: tc50, borderColor: tc500 + "60" } : {}}>
                              <p className="text-xs font-medium" style={editTemplate.billing_rule === opt.value ? { color: tc500 } : {}}>{opt.label}</p>
                              <p className="text-[10px] text-slate-400">{opt.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={() => setEditTemplate(null)}>Cancelar</Button>
                        <Button className="text-white" style={{ backgroundColor: tc500 }} onClick={() => saveTemplate(editTemplate)}>Salvar modelo</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><XCircle className="w-5 h-5" />Desconectar Asaas?</AlertDialogTitle>
            <AlertDialogDescription>As cobranças não serão mais sincronizadas. Cobranças já emitidas permanecem ativas no Asaas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDisconnect}>Desconectar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTemplateTarget} onOpenChange={v => { if (!v) setDeleteTemplateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" />Excluir modelo?</AlertDialogTitle>
            <AlertDialogDescription>O modelo "{deleteTemplateTarget?.name}" será excluído permanentemente. Cobranças já emitidas com este modelo não serão afetadas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={async () => {
              if (!deleteTemplateTarget) return;
              try { await axios.delete(`${API}/asaas/billing-templates/${deleteTemplateTarget.id}`); fetchTemplates(); toast.success("Modelo excluído!"); } catch { toast.error("Erro"); }
              setDeleteTemplateTarget(null);
            }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


export default function IntegracoesPage() {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50  = appearance.colors?.["50"]  || "#EFF6FF";

  return (
    <div data-testid="integracoes-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <Wifi className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">
            {t("configuracoes.integracoes.title")}
          </h1>
          <p className="text-slate-500 mt-0.5">{t("configuracoes.integracoes.subtitle")}</p>
        </div>
      </div>

      <div className="space-y-4">
        <WhatsAppEvolutionCard tc500={tc500} tc50={tc50} />
        <EmailSMTPCard tc500={tc500} tc50={tc50} />
        <AISummaryCard tc500={tc500} tc50={tc50} />
        <GoToConnectCard tc500={tc500} tc50={tc50} />
        <AWSS3Card tc500={tc500} tc50={tc50} />
        <AutentiqueCard tc500={tc500} tc50={tc50} />
        <AsaasCard tc500={tc500} tc50={tc50} />
      </div>
    </div>
  );
}
